import { useMemo, useState } from "react";
import { Badge } from "flowbite-react";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import { formatToReal } from "../../../utils/utils";

const AcoesCiveis = ({
  acoesCiveis = [],
  quantidadeTotal,
  primeiroValor,
  valorTotal,
  dataMaior,
}) => {
  const [expandido, setExpandido] = useState(false);

  const hasDistribuicao = acoesCiveis.some((acao) => {
    const distribuicao = acao?.distribuicao;
    if (distribuicao === null || distribuicao === undefined) return false;
    const distribuicaoNormalizada = String(distribuicao).trim();
    return distribuicaoNormalizada !== "" && distribuicaoNormalizada !== "-";
  });

  const gridColsClass = hasDistribuicao ? "grid-cols-5" : "grid-cols-4";

  const LIMITE = 5;
  const total = acoesCiveis.length;
  const restantes = Math.max(total - LIMITE, 0);

  const listaExibida = useMemo(
    () => (expandido ? acoesCiveis : acoesCiveis.slice(0, LIMITE)),
    [expandido, acoesCiveis],
  );

  return (
    <>
      {/* Resumo visual */}
      <div className="grid gap-3 mb-5 grid-cols-3">
        {quantidadeTotal && (
          <div className="flex items-center justify-between rounded-2xl border border-red-200 bg-red-50 px-4 py-1 text-sm shadow-sm">
            <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
              Quantidade total
            </div>
            <div className="text-lg font-semibold text-slate-900">
              {quantidadeTotal}
            </div>
          </div>
        )}
        {dataMaior && (
          <div className="flex items-center justify-between rounded-2xl border border-red-200 bg-red-50 px-4 py-1 text-sm shadow-sm">
            <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
              Data maior
            </div>
            <div className="text-lg font-semibold text-rose-600">
              {dataMaior}
            </div>
          </div>
        )}
        {valorTotal && (
          <div className="flex items-center justify-between rounded-2xl border border-red-200 bg-red-50 px-4 py-1 text-sm shadow-sm">
            <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
              Valor total
            </div>
            <div className="text-lg font-semibold text-rose-600">
              {formatToReal(valorTotal)}
            </div>
          </div>
        )}
      </div>

      {/* Tabela + Fade quando colapsado */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
          <div
            className={`grid gap-4 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500 ${gridColsClass}`}
          >
            <div>Processo</div>
            <div>Ação</div>
            <div>Autor</div>
            {hasDistribuicao && <div>Distribuição</div>}
            <div className="text-right">Valor</div>
          </div>
        </div>

        {/* Área com transição de altura */}
        <div
          className={`transition-all duration-300 ease-out ${
            expandido ? "max-h-[9999px]" : "max-h-auto"
          } overflow-hidden`}
        >
          {listaExibida.map((acao, index) => (
            <div
              key={index}
              className={`grid items-center gap-4 px-4 py-3 text-sm transition-colors ${gridColsClass} ${
                index % 2 === 0 ? "bg-white" : "bg-slate-50/40"
              } hover:bg-slate-50`}
            >
              <div className="font-medium text-slate-700">
                {acao?.processo || "-"}
              </div>
              <div className="text-slate-700">{acao?.acao || "-"}</div>
              <div className="text-slate-700">{acao?.autor || "-"}</div>
              {hasDistribuicao && (
                <div className="text-slate-700">
                  {acao?.distribuicao || "-"}
                </div>
              )}
              <div className="text-right font-semibold text-rose-600 tabular-nums">
                {formatToReal(acao?.valor || 0)}
              </div>
            </div>
          ))}
        </div>

        {/* Fade indicando que há mais quando colapsado */}
        {!expandido && total > LIMITE && (
          <div className="pointer-events-none absolute inset-x-0 bottom-[52px] h-16 bg-gradient-to-t from-white to-white/0" />
        )}

        {/* Footer com contador + botão */}
        {total > LIMITE && (
          <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-white px-4 py-3">
            <button
              type="button"
              onClick={() => setExpandido((v) => !v)}
              aria-expanded={expandido}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300"
            >
              {expandido ? (
                <>
                  <HiChevronUp className="text-lg" />
                  Ver menos
                </>
              ) : (
                <>
                  <HiChevronDown className="text-lg" />
                  Ver mais
                </>
              )}
            </button>
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <Badge color="gray">
                Exibindo {Math.min(LIMITE, total)} de {total}
              </Badge>
              {!expandido && restantes > 0 && (
                <span className="text-slate-500">(+{restantes} restantes)</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Aviso quando colapsado */}
      {!expandido && total > LIMITE && (
        <div className="text-sm mt-3 text-slate-500 text-center">
          Exibindo as últimas {LIMITE} ocorrências.
        </div>
      )}
    </>
  );
};

export default AcoesCiveis;
